import { useEffect, useState } from "react";

interface ConfirmDeleteDialogProps {
  title: string;
  message: string;
  /** Button label — defaults to "DELETE". Use "ARCHIVE" for soft-delete cases. */
  confirmLabel?: string;
  /** Throws on failure — error shown inline. */
  onConfirm: () => Promise<void>;
  onCancel: () => void;
}

/**
 * Reusable destructive-confirmation dialog. Same WriteConfirm boundary as
 * the other dialogs: Escape/CANCEL only, no backdrop dismiss, "DELETING…"
 * state during the call, inline error if the API throws.
 */
export function ConfirmDeleteDialog({
  title,
  message,
  confirmLabel = "DELETE",
  onConfirm,
  onCancel,
}: ConfirmDeleteDialogProps) {
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !running) onCancel();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onCancel, running]);

  const handleConfirm = async () => {
    setError(null);
    setRunning(true);
    try {
      await onConfirm();
    } catch (err) {
      setError(err instanceof Error ? err.message : "delete_failed");
      setRunning(false);
    }
    // Parent unmounts on success.
  };

  return (
    <div className="write-confirm-backdrop" role="dialog" aria-modal="true">
      <div className="write-confirm-card confirm-delete-card">
        <div className="write-confirm-title destructive">{title}</div>
        <div className="confirm-delete-body">{message}</div>
        {error && <div className="write-confirm-error">⚠ {error}</div>}
        <div className="write-confirm-actions">
          <button
            className="write-confirm-cancel"
            onClick={onCancel}
            disabled={running}
          >
            CANCEL
          </button>
          <button
            className="write-confirm-confirm confirm-delete-confirm"
            onClick={handleConfirm}
            disabled={running}
            autoFocus
          >
            {running ? "DELETING…" : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
